/**
 * Admin panel guided tours (Bill 2026-10-07 role walkthroughs). Every step
 * points at a live surface; nothing is clicked or saved for the person.
 * First matching tour starts on first open; bump `version` when steps change.
 */
import type { TourDef } from '@retail-ops/guided-tour';

export const TOURS: ReadonlyArray<TourDef> = [
  {
    id: 'admin-panel',
    version: 2,
    title: 'Admin panel: people and settings',
    summary: 'Domains, giving people access, Test and Training mode, bank-wide settings',
    roles: ['Admin', 'SuperAdmin'],
    steps: [
      {
        route: '/retail',
        title: 'The Admin panel',
        body: (
          <>
            <p>Every system setting for Retail Operations lives here: who can sign in and what they can do, branches, products, documents, compliance rules and bank-wide switches.</p>
            <p>Changes apply to everyone. Access changes reach a person the next time they open the app, within about 5 minutes.</p>
          </>
        ),
      },
      {
        target: 'admin-tiles',
        title: 'Pick a domain',
        body: <p>Each tile is one area of configuration. You only see the tiles your role can change.</p>,
      },
      {
        target: 'tile-identity-access',
        title: 'Identity & Access',
        body: <p>The people who can sign in, the roles they hold, and their mode (live, Test or Training).</p>,
      },
      {
        target: 'users-filter',
        route: '/retail/identity/users',
        title: 'Find a person',
        body: <p>Filter by e-mail, name, operator initials, branch or title. <strong>Add user</strong> brings someone in from the bank&rsquo;s directory.</p>,
      },
      {
        target: 'users-filter-strip',
        title: 'Narrow the list',
        body: <p>Filter by role, by active or inactive, or by mode flag - handy for checking who is in Test mode.</p>,
      },
      {
        title: 'Changing someone’s access',
        body: (
          <>
            <p>Click <strong>Edit</strong> on a person. Under <strong>Admin controls</strong>:</p>
            <ul>
              <li><strong>Test mode</strong> - real Core Director, test customers only (last name Test with a 666 tax ID, or a 000 CIF). This is how pilot testers are set.</li>
              <li><strong>Training mode</strong> - everything simulated, for learning the screens. The two cannot both be on.</li>
              <li><strong>Roles</strong> - tick what they need. The list under it shows their access after you save.</li>
            </ul>
            <p><strong>Save</strong> shows exactly what will change before anything is written, and every change is kept in <strong>Access history</strong>. <strong>Guided tours</strong> in the same window resets these walkthroughs for that person.</p>
          </>
        ),
      },
      {
        target: 'settings-recent',
        route: '/retail/system/settings',
        title: 'Bank-wide settings',
        body: (
          <>
            <p>System → Settings holds the switches that change behaviour for everyone, each with a description. Tick this to see only what changed recently.</p>
            <p>For example <code>tours.enabled</code> decides whether these walkthroughs start by themselves.</p>
          </>
        ),
      },
      {
        target: 'tile-change-history',
        route: '/retail',
        title: 'Change history',
        body: <p>Every change to settings, disclosures and configuration - from any screen or panel - with who, when, before and after, and why. If something was set wrong, find the change and <strong>Restore</strong> it; the restore is recorded too.</p>,
      },
      {
        target: 'report-problem',
        title: 'Report a problem or suggest a change',
        body: <p>The lifebuoy, bottom-left. A picture of the screen and the recent activity go with your report. Suggestions go to management for review first.</p>,
      },
      {
        target: 'tour-start',
        route: '/retail',
        title: 'Take it again',
        body: <p><strong>Guided tour</strong> in the top bar runs this walkthrough again whenever you want it.</p>,
      },
    ],
  },
  {
    id: 'admin-panel-content',
    version: 2,
    title: 'Admin panel: your areas',
    summary: 'Finding the configuration your role looks after',
    roles: ['RetailAdmin', 'SysOpsManager', 'RiskManager', 'DisclosureManager'],
    steps: [
      {
        route: '/retail',
        title: 'The Admin panel',
        body: <p>The configuration behind Retail Operations. Changes apply to everyone, so most screens show what will change before they save.</p>,
      },
      {
        target: 'admin-tiles',
        title: 'Your domains',
        body: <p>You see the tiles your role looks after - for example products, documents and disclosures, compliance rules or monitoring. Inside each, the tabs across the top are its sections.</p>,
      },
      {
        target: 'tile-change-history',
        title: 'Change history',
        body: <p>Every configuration change, with who, when, before and after, and why. Search it to see what changed, and restore a change that was set wrong.</p>,
      },
      {
        target: 'report-problem',
        title: 'Report a problem or suggest a change',
        body: <p>The lifebuoy, bottom-left. A picture of the screen and the recent activity go with your report.</p>,
      },
      {
        target: 'tour-start',
        title: 'Take it again',
        body: <p><strong>Guided tour</strong> in the top bar runs this again.</p>,
      },
    ],
  },
];
